import { DateTime, Interval } from 'luxon';
import { LitElement, html, css } from '~/lib/bn-lit-element';
import { sharedStyles } from '../styles/shared-styles';
import './bn-icon';
import './bn-surface';

class BnCalendar extends LitElement {
  constructor() {
    super();
    this.currentMonth = DateTime.local().startOf('month');
  }

  static get properties() {
    return {
      currentMonth: { type: Object },
      selectedDate: { type: Object },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        display: inline-block;
        width: 100%;
        user-select: none;
      }
      .header {
        display: flex;
        align-items: center;
        margin-bottom: .5rem;
      }
      .header .title {
        flex-grow: 1;
        padding: .5rem;
      }
      .header .title .month-name {
        font-weight: bold;
      }
      .header button {
        padding: .5rem;
      }
      .header button.nav {
        padding: .5em 1em;
        margin-right: .5rem;
        color: var(--blue-60);
      }
      .header button.today {
        font-size: .8em;
      }
      .month {
        width: 100%;
        background-color: var(--white-base);
        border: 1px solid var(--blue-50);
        border-width: 1px 0 0 1px;
        display: grid;
        grid-template-columns: repeat(7, 1fr);
      }
      .heading {
        padding: .5rem;
        font-size: .8rem;
        border: 1px solid var(--blue-50);
        border-width: 0 1px 1px 0;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .week {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
      }
      .day {
        display: flex;
        flex-wrap: wrap;
        padding: .5em;
        background-color: var(--white-base);
        border: 1px solid var(--blue-50);
        border-width: 0 1px 1px 0;
        text-overflow: ellipsis;
        overflow: hidden;
      }
      .day .number {
        width: 1.5em;
        height: 1em;
        font-size: .8em;
        padding: .2em .4em;
        border-radius: 1em;
        margin: -.3em .3em .3em -.3em;
        color: var(--gray-80);
        text-align: center;
        display: inline-block;
      }
      .day .slots {
        flex-grow: 1;
      }
      .day.weekend {
        background-color: var(--gray-10);
      }
      .day.faded {
        background-color: var(--gray-20);
        color: var(--gray-60);
      }
      .day:hover {
        cursor: pointer;
        background-color: var(--hover-color);
      }
      .day.active {
        background-color: var(--blue-40);
      }
      .day.active .number {
        font-weight: bold;
      }
      .day.today .number {
        background-color: var(--blue-10);
        color: var(--blue-60);
        height: 1rem;
      }
    `];
  }

  render() {
    const first = this.currentMonth;
    const last = first.endOf('month');
    // adjust to week starting on sunday (which luxon doesn't support)
    const start = first.startOf('week').minus({ day: 1 });
    const end = last.endOf('week').hasSame(last, 'day') ? last.plus({ day: 6 }) : last.endOf('week').minus({ day: 1 });
    const days = Interval.fromDateTimes(start, end).splitBy({ days: 1 }).map((i) => i.start);
    const weekdays = days.slice(0,7).map(d => d.weekdayLong);
    return html`
      <bn-surface>
        <div class="header">
          <button class="nav button button--plain" @click="${this._prev}"><bn-icon name="far fa-less-than"></bn-icon></button>
          <button class="nav button button--plain" @click="${this._next}"><bn-icon name="far fa-greater-than"></bn-icon></button>
          <div class="title">
            <span class="month-name">${this.currentMonth.monthLong}</span>
            <span class="year">${this.currentMonth.year}</span>
          </div>
          <button class="today button button--contained" @click="${() => this._selectToday()}">Today</button>
        </div>
        <div class="boxes">
          <div class="month">
            ${weekdays.map(day => html`<div class="heading">${day}</div>`)}
            ${days.map((day) => this._renderDay(day))}
          </div>
        </div>
      </bn-surface>
    `;
  }

  _renderDay(day) {
    const classes = ['day'];
    if (day.month !== this.currentMonth.month) {
      classes.push('faded');
    }
    if (this.selectedDate && day.hasSame(this.selectedDate, 'day')) {
      classes.push('active');
    }
    if (day.weekday > 5) {
      classes.push('weekend');
    }
    if (day.hasSame(DateTime.local(), 'day')) {
      classes.push('today');
    }
    return html`
      <div class="${classes.join(' ')}" @click="${() => this._selectDay(day)}">
        <div class="number-box">
          <span class="number">${day.day}</span>
        </div>
        <div class="slots">
          <slot name="${day.toFormat('yyyy-MM-dd')}"></slot>
        </div>
      </div>
    `;
  }

  firstUpdated() {
    this._selectToday();
  }

  _prev() {
    this.currentMonth = this.currentMonth.minus({ month: 1 });
  }

  _next() {
    this.currentMonth = this.currentMonth.plus({ month: 1 });
  }

  _selectDay(date) {
    this.selectedDate = date;
    this.dispatchEvent(new CustomEvent('select', { detail: { date } }));
  }

  _selectToday() {
    const now = DateTime.local();
    this.currentMonth = now.startOf('month');
    this._selectDay(now);
  }
}

customElements.define('bn-calendar', BnCalendar);
